/**
 * Montana State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * Montana home health agencies are regulated under general healthcare facility
 * licensure standards, NOT dedicated home health agency rules.
 * 
 * STATE LICENSING:
 * - ARM 37.106.3 (Subchapter 3): Construction and Minimum Standards for All Health Care Facilities
 * - These general standards apply to ALL licensed healthcare facilities including home health
 * - Key Rules: 37.106.301 (Definitions), 37.106.310 (Licensing), 37.106.313 (Infection Control),
 *   37.106.314 (Medical Records), 37.106.315 (Employee Files), 37.106.322 (Disaster Plan),
 *   37.106.330 (Written Policies)
 * 
 * FEDERAL REQUIREMENTS (for Medicare-certified agencies):
 * - 42 CFR 484: Medicare Home Health Conditions of Participation
 * - CMS State Operations Manual Appendix B
 * 
 * Regulatory Body: Montana Department of Public Health and Human Services (DPHHS)
 * Division: Office of Inspector General - Licensure Bureau
 * Phone: (406) 444-2037
 * Website: https://dphhs.mt.gov/qad/licensure
 * 
 * NOTE: Previous version incorrectly cited ARM 37.106.14xx (Chemical Dependency
 * Facility rules) as home health agency rules. ARM 37.106.14 is for Chemical
 * Dependency Facilities, NOT home health agencies. This version corrects all citations.
 * 
 * Montana does not have extensive state-specific home health regulations beyond
 * the general healthcare facility standards. Medicare-certified agencies primarily
 * follow federal 42 CFR 484 requirements.
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const MONTANA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'MT-A1',
    itemId: 'A1',
    stateCode: 'MT',
    title: 'DPHHS Home Health Agency License Status',
    description: 'Montana requires all home health agencies to be licensed by DPHHS under ARM 37.106.310',
    severity: 'high',
    regulationCode: '37.106.310',
    regulationName: 'License',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure/healthcarefacilitylicensure/lbfacilityapplications/lbhomehealthagency',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from Montana DPHHS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health agency licenses may be issued for 1-3 years per ARM 37.106.310(3)',
      },
      {
        id: 'q2',
        question: 'What is your DPHHS license expiration date?',
        inputType: 'date',
        helpText: 'Found on your DPHHS license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'What type of license do you hold?',
        inputType: 'select',
        options: [
          { value: '3year', label: '3-year license (deficiency-free or accredited)' },
          { value: '2year', label: '2-year license (minor deficiencies)' },
          { value: '1year', label: '1-year license (new agency, change of ownership, or deficiencies)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per ARM 37.106.310(3), license duration depends on survey results and accreditation status',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your Montana DPHHS Home Health Agency license is current and active per ARM 37.106.310.',
      atRisk: 'Your DPHHS license expires within 90 days. Submit renewal application to DPHHS immediately.',
      nonCompliant: 'Operating a home health agency in Montana without a valid DPHHS license violates MCA 50-5-103. Contact DPHHS at (406) 444-2037.',
      unknown: 'Verify your DPHHS license status by calling (406) 444-2037.',
    },
    stateNotes: [
      'License duration: 1-3 years depending on survey results per ARM 37.106.310(3)',
      '3-year license available for deficiency-free survey or accreditation',
      '2-year license for minor deficiencies not affecting health/safety',
      '1-year license for new agencies, ownership changes, or significant deficiencies',
      'Premises open to inspection by DPHHS at all reasonable times per ARM 37.106.310(4)',
    ],
  },

  {
    id: 'MT-A2',
    itemId: 'A2',
    stateCode: 'MT',
    title: 'Business Entity Registration',
    description: 'Montana requires proper business registration with Secretary of State',
    severity: 'high',
    regulationCode: 'MCA 35-1-201',
    regulationName: 'Business Entity',
    agencyName: 'Montana Secretary of State',
    agencyAbbr: 'SOS',
    agencyPhone: '(406) 444-2034',
    agencyUrl: 'https://sosmt.gov/business/',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency properly registered with the Montana Secretary of State?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your annual report current with Secretary of State?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Unsure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Business entity registration is current with Montana Secretary of State.',
      atRisk: 'Annual report may not be current. File with Secretary of State.',
      nonCompliant: 'Agency is not properly registered in Montana. Register with Secretary of State immediately.',
      unknown: 'Verify business registration with Montana Secretary of State.',
    },
    stateNotes: [
      'Business registration required under MCA 35-1-201',
      'Annual reports must be filed to maintain good standing',
      'Contact Secretary of State at (406) 444-2034',
      'Online services at sosmt.gov/business',
    ],
  },

  {
    id: 'MT-A3',
    itemId: 'A3',
    stateCode: 'MT',
    title: 'Written Policies and Procedures (ARM 37.106.330)',
    description: 'Montana requires current written policy and procedure manual',
    severity: 'high',
    regulationCode: '37.106.330',
    regulationName: 'Policies',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain a current written policy and procedure manual per ARM 37.106.330?',
        inputType: 'checklist',
        options: [
          { value: 'describes_services', label: 'Describes all services provided in the facility' },
          { value: 'available_staff', label: 'Available to staff' },
          { value: 'available_residents', label: 'Available to residents and family members' },
          { value: 'available_dept', label: 'Available to DPHHS for inspection' },
          { value: 'annual_review', label: 'Reviewed at least annually by administrator or medical director' },
          { value: 'documented_review', label: 'Written documentation of the review maintained' },
          { value: 'complied_with', label: 'Complied with by all facility personnel and agents' },
        ],
        required: true,
        helpText: 'ARM 37.106.330 requires a current written policy manual that is available to staff, residents, families, and DPHHS',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Montana ARM 37.106.330 requirements.',
      atRisk: 'Your policy manual may be incomplete per ARM 37.106.330. Ensure annual review is documented.',
      nonCompliant: 'Policy and procedure manual does not meet ARM 37.106.330 requirements. Develop and maintain current written policies.',
      unknown: 'Review your policies against ARM 37.106.330 requirements.',
    },
    stateNotes: [
      'Policy manual must describe ALL services provided',
      'Must be available to staff, residents, families, and DPHHS',
      'Annual review required by administrator or medical director',
      'Written documentation of review must be maintained',
      'All personnel and agents must comply with policies',
    ],
  },

  {
    id: 'MT-A4',
    itemId: 'A4',
    stateCode: 'MT',
    title: 'Medicare Certification Status (42 CFR 484)',
    description: 'Medicare-certified agencies must meet federal Conditions of Participation',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare CoPs',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 633-4227',
    agencyUrl: 'https://www.cms.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Medicare-certified home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your Medicare certification current and in good standing?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, current with no deficiencies' },
          { value: 'deficiencies', label: 'Current but with deficiencies' },
          { value: 'expired', label: 'Expired or suspended' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'current', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'deficiencies', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'expired', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification is current and in good standing (or agency is not Medicare-certified).',
      atRisk: 'Medicare certification has deficiencies. Address Plan of Correction requirements.',
      nonCompliant: 'Medicare certification has expired or been suspended. Contact CMS and DPHHS.',
      unknown: 'Verify Medicare certification status with DPHHS.',
    },
    stateNotes: [
      'Medicare-certified agencies must meet 42 CFR 484 requirements',
      'DPHHS conducts surveys on behalf of CMS',
      'Many Montana home health requirements come from federal Medicare CoPs',
      'State licensure is separate from Medicare certification',
    ],
  },

  {
    id: 'MT-A5',
    itemId: 'A5',
    stateCode: 'MT',
    title: 'Workers Compensation Insurance',
    description: 'Montana requires workers compensation coverage for employers',
    severity: 'high',
    regulationCode: 'MCA 39-71',
    regulationName: 'Workers Comp',
    agencyName: 'Montana State Fund',
    agencyAbbr: 'MSF',
    agencyPhone: '(406) 444-6500',
    agencyUrl: 'https://www.montanastatefund.com',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain workers compensation insurance?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, current coverage' },
          { value: 'exempt', label: 'Exempt (sole proprietor with no employees)' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'includes', value: ['yes', 'exempt'], result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers compensation coverage meets Montana requirements under MCA 39-71.',
      atRisk: 'Verify workers compensation coverage is adequate.',
      nonCompliant: 'Missing required workers compensation insurance. Contact Montana State Fund.',
      unknown: 'Verify workers compensation status.',
    },
    stateNotes: [
      'Required under MCA 39-71 for employers',
      'Montana State Fund: (406) 444-6500',
      'Sole proprietors without employees may be exempt',
      'Coverage through Montana State Fund or private insurer',
    ],
  },

  {
    id: 'MT-A6',
    itemId: 'A6',
    stateCode: 'MT',
    title: 'Professional Liability Insurance',
    description: 'Montana recommends professional liability insurance for healthcare providers',
    severity: 'medium',
    regulationCode: 'Industry Standard',
    regulationName: 'Liability Insurance',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain professional liability insurance?',
        inputType: 'select',
        options: [
          { value: 'adequate', label: 'Yes, $1M per occurrence / $3M aggregate or higher' },
          { value: 'low', label: 'Yes, but below recommended levels' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'adequate', result: 'COMPLIANT' },
      { condition: 'equals', value: 'low', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional liability insurance meets recommended coverage levels.',
      atRisk: 'Professional liability coverage may be inadequate. Review policy limits.',
      nonCompliant: 'No professional liability insurance creates significant legal risk.',
      unknown: 'Verify insurance coverage levels.',
    },
    stateNotes: [
      'Industry standard: $1M per occurrence / $3M aggregate',
      'Include professional liability and general liability coverage',
      'Consider umbrella policy for additional protection',
      'Verify coverage annually with insurance provider',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'MT-B1',
    itemId: 'B1',
    stateCode: 'MT',
    title: 'Employee File Requirements (ARM 37.106.315)',
    description: 'Montana requires specific documents in each employee\'s personnel file',
    severity: 'high',
    regulationCode: '37.106.315',
    regulationName: 'Personnel Files',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY employee\'s personnel file per ARM 37.106.315?',
        inputType: 'checklist',
        options: [
          { value: 'name', label: 'Employee\'s name' },
          { value: 'job_description', label: 'Job description signed by the employee' },
          { value: 'orientation', label: 'Documentation of employee orientation, signed by the employee' },
          { value: 'credentials', label: 'Copy of current credentials, certification, or professional licenses' },
        ],
        required: true,
        helpText: 'ARM 37.106.315(2) specifies minimum contents for employee files',
      },
      {
        id: 'q2',
        question: 'Are employee files available for DPHHS inspection within 24 hours if maintained off-site?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, or files are maintained on-site' },
          { value: 'no', label: 'No / Not sure' },
        ],
        helpText: 'Per ARM 37.106.315(1), off-site files must be available within 24 hours of request',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Montana-required documentation per ARM 37.106.315.',
      atRisk: 'Some personnel files may be missing required documents per ARM 37.106.315.',
      nonCompliant: 'Personnel files are missing critical documentation required by ARM 37.106.315. DPHHS surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against ARM 37.106.315 requirements.',
    },
    stateNotes: [
      'ARM 37.106.315 requires: employee name, signed job description, signed orientation documentation, current credentials/licenses',
      'Files may be inspected by DPHHS at any time',
      'Off-site files must be available within 24 hours of DPHHS request',
      'Volunteers performing staff duties must have orientation documentation',
    ],
  },

  {
    id: 'MT-B2',
    itemId: 'B2',
    stateCode: 'MT',
    title: 'Professional License Verification',
    description: 'Montana requires verification of all professional licenses',
    severity: 'high',
    regulationCode: '37.106.315(2)(d)',
    regulationName: 'License Verification',
    agencyName: 'Montana Board of Nursing',
    agencyAbbr: 'MTBON',
    agencyPhone: '(406) 444-6880',
    agencyUrl: 'https://boards.bsd.dli.mt.gov/nursing',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses with Montana licensing boards before hire and track renewals?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
        helpText: 'ARM 37.106.315(2)(d) requires copy of current credentials, certification, or licenses in file',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process meets Montana requirements.',
      atRisk: 'License verification at hire only is insufficient. Implement ongoing verification to catch expired licenses.',
      nonCompliant: 'No license verification process creates significant risk. Implement verification with Montana licensing boards.',
      unknown: 'Verify license verification procedures are in place.',
    },
    stateNotes: [
      'ARM 37.106.315(2)(d) requires CURRENT credentials/licenses on file',
      'Montana Board of Nursing: (406) 444-6880',
      'Verify licenses online at boards.bsd.dli.mt.gov',
      'Track renewal dates to ensure credentials remain current',
    ],
  },

  {
    id: 'MT-B3',
    itemId: 'B3',
    stateCode: 'MT',
    title: 'Volunteer Documentation (ARM 37.106.315)',
    description: 'Montana requires documentation for volunteers performing staff duties',
    severity: 'medium',
    regulationCode: '37.106.315(3)',
    regulationName: 'Volunteers',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency use volunteers who perform duties commonly performed by staff?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For volunteers, do you maintain files documenting:',
        inputType: 'checklist',
        options: [
          { value: 'orientation_facility', label: 'Orientation to the facility and its residents' },
          { value: 'orientation_duties', label: 'Orientation to and training of duties to be performed' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'ARM 37.106.315(3) requires documentation for volunteers performing staff duties',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 1, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 1, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Volunteer documentation meets ARM 37.106.315(3) requirements (or no volunteers used).',
      atRisk: 'Volunteer documentation may be incomplete per ARM 37.106.315(3).',
      nonCompliant: 'Volunteers are performing duties without required documentation per ARM 37.106.315(3).',
      unknown: 'Review volunteer file requirements.',
    },
    stateNotes: [
      'Volunteers may not be included in staffing plan in lieu of employees',
      'Volunteers performing staff-type duties require documented orientation',
      'Files must document orientation to facility/residents and duties training',
    ],
  },

  // ============================================
  // SECTION C: HEALTH & SAFETY
  // ============================================
  {
    id: 'MT-C1',
    itemId: 'C1',
    stateCode: 'MT',
    title: 'Infection Prevention and Control (ARM 37.106.313)',
    description: 'Montana requires a comprehensive infection prevention and control program',
    severity: 'high',
    regulationCode: '37.106.313',
    regulationName: 'Infection Control',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an infection prevention and control program per ARM 37.106.313?',
        inputType: 'checklist',
        options: [
          { value: 'written_policies', label: 'Written infection prevention and control policies and procedures' },
          { value: 'high_risk', label: 'Procedures to identify high risk individuals' },
          { value: 'methods', label: 'Identification of methods to protect, contain, or minimize risk' },
          { value: 'annual_review', label: 'Policies reviewed at least annually' },
          { value: 'responsible_person', label: 'Administrator or infection control officer responsible for program' },
        ],
        required: true,
        helpText: 'ARM 37.106.313 requires development, implementation, and annual review of infection control policies',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection prevention and control program meets Montana ARM 37.106.313 requirements.',
      atRisk: 'Infection control program may be incomplete per ARM 37.106.313.',
      nonCompliant: 'Infection control program does not meet ARM 37.106.313 requirements.',
      unknown: 'Review infection control program against ARM 37.106.313.',
    },
    stateNotes: [
      'Must develop, implement, and review infection control policies at least annually',
      'Must include procedures to identify high risk individuals',
      'Must identify methods to protect, contain, or minimize risk to patients, staff, and visitors',
      'Administrator or designated infection control officer is responsible for program quality',
    ],
  },

  {
    id: 'MT-C2',
    itemId: 'C2',
    stateCode: 'MT',
    title: 'TB Screening',
    description: 'Montana follows CDC guidelines for TB screening of healthcare workers',
    severity: 'high',
    regulationCode: 'CDC Guidelines',
    regulationName: 'TB Screening',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff with patient contact have current TB screening documentation?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current TB screening' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'TB screening should be completed before patient contact per CDC healthcare worker guidelines',
      },
    ],
    scoringRules: [
      // 1. NON_COMPLIANT - Check for clear non-compliance first
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
      // 2. COMPLIANT - Check for full compliance
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      // 3. AT_RISK - Partial compliance / needs attention
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all staff.',
      atRisk: 'Some TB screenings may be missing. Review all staff files.',
      nonCompliant: 'Staff are working without valid TB screening. Implement TB screening program.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'Follow CDC guidelines for healthcare worker TB screening',
      'Baseline screening required before patient contact',
      'Montana DPHHS TB Program: (406) 444-0273',
      'Document all TB screenings in personnel files',
    ],
  },

  {
    id: 'MT-C3',
    itemId: 'C3',
    stateCode: 'MT',
    title: 'Disaster Plan (ARM 37.106.322)',
    description: 'Montana requires written disaster planning for healthcare facilities',
    severity: 'high',
    regulationCode: '37.106.322',
    regulationName: 'Emergency Plan',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written disaster plan per ARM 37.106.322?',
        inputType: 'checklist',
        options: [
          { value: 'written_plan', label: 'Written disaster plan for the facility' },
          { value: 'natural_disasters', label: 'Addresses natural disasters (blizzards, wildfires, floods)' },
          { value: 'man_caused', label: 'Addresses man-caused disasters' },
          { value: 'community_coordination', label: 'Developed in conjunction with community emergency services' },
          { value: 'annual_review', label: 'Documented annual review or exercise conducted' },
          { value: 'staff_training', label: 'Staff trained on emergency procedures' },
        ],
        required: true,
        helpText: 'ARM 37.106.322 requires written disaster plan with annual review/exercise',
      },
      {
        id: 'q2',
        question: 'Does your disaster plan documentation include required elements per ARM 37.106.322(2)?',
        inputType: 'checklist',
        options: [
          { value: 'date_time', label: 'Date and time of review or exercise' },
          { value: 'staff_involved', label: 'Names of staff involved' },
          { value: 'other_facilities', label: 'Names of other facilities involved (if any)' },
          { value: 'other_persons', label: 'Names of other persons involved' },
          { value: 'description', label: 'Description of all phases and suggestions for improvement' },
          { value: 'signature', label: 'Signature of person conducting review' },
        ],
        helpText: 'ARM 37.106.322(2) specifies required documentation elements',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Disaster plan meets ARM 37.106.322 requirements.',
      atRisk: 'Disaster plan may be incomplete per ARM 37.106.322.',
      nonCompliant: 'Disaster plan does not meet ARM 37.106.322 requirements.',
      unknown: 'Review disaster plan against ARM 37.106.322 requirements.',
    },
    stateNotes: [
      'Written disaster plan required for natural and man-caused disasters',
      'Annual review or physical exercise required',
      'Documentation must be retained for minimum 3 years',
      'Consider Montana-specific hazards: blizzards, wildfires, rural access issues',
      'Fire drills required at least 4x annually for facilities housing patients',
    ],
  },

  // ============================================
  // SECTION D: MEDICAL RECORDS
  // ============================================
  {
    id: 'MT-D1',
    itemId: 'D1',
    stateCode: 'MT',
    title: 'Medical Records (ARM 37.106.314)',
    description: 'Montana requires safe, secure, and confidential medical records',
    severity: 'high',
    regulationCode: '37.106.314',
    regulationName: 'Clinical Records',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain medical records per ARM 37.106.314?',
        inputType: 'checklist',
        options: [
          { value: 'initiated', label: 'Medical record initiated for each patient/client' },
          { value: 'safe', label: 'Records maintained in a safe environment' },
          { value: 'secure', label: 'Records maintained securely' },
          { value: 'confidential', label: 'Records maintained confidentially' },
        ],
        required: true,
        helpText: 'ARM 37.106.314(1) requires safe, secure, and confidential medical records for each patient',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medical record practices meet ARM 37.106.314 requirements.',
      atRisk: 'Medical record practices may not fully meet ARM 37.106.314 requirements.',
      nonCompliant: 'Medical record practices do not meet ARM 37.106.314 requirements.',
      unknown: 'Review medical record practices against ARM 37.106.314.',
    },
    stateNotes: [
      'ARM 37.106.314(1) requires safe, secure, confidential records',
      'Medical record initiated for each patient/client',
      'Records available for DPHHS inspection',
      'Comply with HIPAA 45 CFR Parts 160-164',
    ],
  },

  {
    id: 'MT-D2',
    itemId: 'D2',
    stateCode: 'MT',
    title: 'Record Retention (ARM 37.106.314)',
    description: 'Montana requires 6-year retention for patient medical records',
    severity: 'high',
    regulationCode: '37.106.314(2)',
    regulationName: 'Record Retention',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient medical records?',
        inputType: 'select',
        options: [
          { value: '6plus', label: '6 years or longer following discharge or death (meets MT requirement)' },
          { value: '5', label: '5 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'ARM 37.106.314(2): Medical records retained no less than 6 years following discharge or death, or upon closure',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '6plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '5', result: 'AT_RISK' },
      { condition: 'includes', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Montana\'s 6-year requirement under ARM 37.106.314(2).',
      atRisk: 'Montana requires 6-year record retention per ARM 37.106.314(2). Update your policy.',
      nonCompliant: 'Your record retention does not meet Montana\'s 6-year requirement under ARM 37.106.314(2).',
      unknown: 'Review record retention requirements under ARM 37.106.314(2).',
    },
    stateNotes: [
      'Minimum 6 years following discharge, death, or facility closure',
      'This is a state requirement - stricter than some other states',
      'Records must be maintained safely and confidentially even during retention period',
    ],
  },

  // ============================================
  // SECTION E: ABUSE REPORTING
  // ============================================
  {
    id: 'MT-E1',
    itemId: 'E1',
    stateCode: 'MT',
    title: 'Mandatory Abuse Reporting Training (MCA 52-3-811)',
    description: 'Montana requires mandatory reporting training for vulnerable adult abuse',
    severity: 'high',
    regulationCode: 'MCA 52-3-811',
    regulationName: 'Abuse Reporting',
    agencyName: 'Montana Adult Protective Services',
    agencyAbbr: 'APS',
    agencyPhone: '(844) 277-9300',
    agencyUrl: 'https://dphhs.mt.gov/sltc/aps',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff receive training on identifying and reporting suspected abuse, neglect, or exploitation of vulnerable adults?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'Yes, during orientation and annually' },
          { value: 'orientation_only', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report to Montana Adult Protective Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, reporting procedures are documented and trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'Montana APS Hotline: 1-844-277-9300 or online at mt.leapsportal.net',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_annual' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_only' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staff are properly trained on mandatory abuse reporting per MCA 52-3-811.',
      atRisk: 'Abuse reporting training may be insufficient. Ensure ongoing training and documented procedures.',
      nonCompliant: 'Failure to train on mandatory abuse reporting violates MCA 52-3-811. Staff who fail to report can face criminal penalties.',
      unknown: 'Verify abuse reporting training meets MCA 52-3-811 requirements.',
    },
    stateNotes: [
      'Healthcare providers are mandatory reporters under MCA 52-3-811',
      'Report to APS at 1-844-277-9300 or online at mt.leapsportal.net',
      'Reports can also be made to county attorney',
      'Failure to report is a misdemeanor per MCA 52-3-817',
      'Reporters are immune from liability for good faith reports per MCA 52-3-814',
    ],
  },

  // ============================================
  // SECTION F: FEDERAL EXCLUSION CHECKS
  // ============================================
  {
    id: 'MT-F1',
    itemId: 'F1',
    stateCode: 'MT',
    title: 'OIG Exclusion List Verification',
    description: 'Federal law requires OIG exclusion list verification for Medicare/Medicaid providers',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://oig.hhs.gov/exclusions/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list for all employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly checks' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification is performed monthly.',
      atRisk: 'OIG verification at hire only is insufficient. Implement monthly checks.',
      nonCompliant: 'No OIG exclusion verification. This can result in severe federal penalties including $10,000+ per service provided by excluded individual.',
      unknown: 'Verify OIG exclusion list checking procedures.',
    },
    stateNotes: [
      'Federal requirement under 42 CFR 1001.1901',
      'OIG LEIE database: https://exclusions.oig.hhs.gov',
      'Penalties exceed $10,000 per service by excluded individual',
      'Document all checks with date, names searched, and results',
    ],
  },

  {
    id: 'MT-F2',
    itemId: 'F2',
    stateCode: 'MT',
    title: 'SAM.gov Exclusion Verification',
    description: 'Federal law requires SAM.gov exclusion verification',
    severity: 'high',
    regulationCode: '2 CFR 180',
    regulationName: 'SAM Exclusion',
    agencyName: 'General Services Administration',
    agencyAbbr: 'GSA',
    agencyPhone: '(866) 606-8220',
    agencyUrl: 'https://sam.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify SAM.gov exclusion status for employees and contractors?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly verification' },
          { value: 'hire_only', label: 'At hire/contract only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'SAM.gov exclusion verification is performed regularly.',
      atRisk: 'SAM verification should be performed more frequently.',
      nonCompliant: 'No SAM exclusion verification. Implement verification process.',
      unknown: 'Verify SAM.gov checking procedures.',
    },
    stateNotes: [
      'Required under 2 CFR 180 for federal program participants',
      'SAM.gov database: https://sam.gov',
      'Check employees, contractors, and vendors',
      'Document all searches with date and results',
    ],
  },

  // ============================================
  // SECTION G: TRAINING & ORIENTATION
  // ============================================
  {
    id: 'MT-G1',
    itemId: 'G1',
    stateCode: 'MT',
    title: 'Employee Orientation (ARM 37.106.315)',
    description: 'Montana requires documented orientation for all employees',
    severity: 'high',
    regulationCode: '37.106.315(2)(c)',
    regulationName: 'Orientation',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new staff complete documented orientation before patient contact?',
        inputType: 'checklist',
        options: [
          { value: 'general', label: 'General agency orientation' },
          { value: 'policies', label: 'Review of agency policies and procedures' },
          { value: 'safety', label: 'Safety and emergency procedures' },
          { value: 'infection', label: 'Infection control' },
          { value: 'hipaa', label: 'HIPAA and confidentiality' },
          { value: 'abuse', label: 'Abuse recognition and mandatory reporting (MCA 52-3-811)' },
          { value: 'job_specific', label: 'Job-specific training' },
          { value: 'signed', label: 'Orientation documentation signed by employee' },
        ],
        required: true,
        helpText: 'ARM 37.106.315(2)(c) requires documentation of employee orientation, signed by the employee',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Orientation training program meets Montana ARM 37.106.315 requirements.',
      atRisk: 'Orientation training may be incomplete. Ensure signed documentation per ARM 37.106.315.',
      nonCompliant: 'Staff are providing services without required orientation training per ARM 37.106.315.',
      unknown: 'Review orientation training program against ARM 37.106.315 requirements.',
    },
    stateNotes: [
      'ARM 37.106.315(2)(c) requires orientation documentation signed by employee',
      'Must be on file before employee provides patient care',
      'Should cover policies, safety, infection control, abuse reporting',
    ],
  },

  {
    id: 'MT-G2',
    itemId: 'G2',
    stateCode: 'MT',
    title: 'HIPAA Training',
    description: 'Federal law requires HIPAA privacy and security training for all staff',
    severity: 'high',
    regulationCode: '45 CFR 164.530(b)',
    regulationName: 'HIPAA Training',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees receive HIPAA training?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, at hire and annually' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'initial_annual', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA training is provided at hire and annually.',
      atRisk: 'HIPAA training should be provided annually, not just at hire.',
      nonCompliant: 'No HIPAA training. This violates federal law (45 CFR 164).',
      unknown: 'Verify HIPAA training requirements.',
    },
    stateNotes: [
      'HIPAA Privacy Rule: 45 CFR 164.530(b) requires training',
      'Training must be provided within reasonable time of hire',
      'Document training completion in personnel files',
      'Include privacy practices, security awareness, and breach procedures',
    ],
  },

  // ============================================
  // SECTION H: PHYSICAL PLANT (if applicable)
  // ============================================
  {
    id: 'MT-H1',
    itemId: 'H1',
    stateCode: 'MT',
    title: 'Physical Plant Maintenance (ARM 37.106.320)',
    description: 'Montana requires facility maintenance program for agency offices',
    severity: 'medium',
    regulationCode: '37.106.320',
    regulationName: 'Equipment',
    agencyName: 'Montana Department of Public Health and Human Services',
    agencyAbbr: 'DPHHS',
    agencyPhone: '(406) 444-2037',
    agencyUrl: 'https://dphhs.mt.gov/qad/licensure',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency maintain its physical location per ARM 37.106.320?',
        inputType: 'checklist',
        options: [
          { value: 'written_program', label: 'Written maintenance program describing procedures' },
          { value: 'good_repair', label: 'Building, grounds, and equipment kept in good repair' },
          { value: 'hazard_free', label: 'Facility kept free from hazards' },
          { value: 'housekeeping', label: 'Housekeeping services provided daily' },
          { value: 'systems', label: 'Electrical, mechanical, plumbing, heating systems operational' },
          { value: 'clean', label: 'Facility kept clean and free of odors' },
        ],
        required: true,
        helpText: 'ARM 37.106.320 applies to facility locations where services are provided or staff are based',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Physical plant maintenance meets ARM 37.106.320 requirements.',
      atRisk: 'Physical plant maintenance may need improvement per ARM 37.106.320.',
      nonCompliant: 'Physical plant maintenance does not meet ARM 37.106.320 requirements.',
      unknown: 'Review physical plant requirements per ARM 37.106.320.',
    },
    stateNotes: [
      'Written maintenance program required',
      'Daily housekeeping services required for facilities',
      'All building systems must be kept operational',
      'Hot water to handwashing/bathing must not exceed 120°F',
    ],
  },
];

export default MONTANA_QUESTIONS;